// Timezone utilities for business location handling
import { format } from 'date-fns';


/**
 * Convert UTC time string (HH:MM) to local business timezone
 */
export function convertUtcTimeToLocal(utcTimeStr: string, businessTimezone: string): string {
  try {
    // Parse the UTC time string (e.g., "04:00") 
    const [hours, minutes] = utcTimeStr.split(':').map(Number);
    
    // Create a date with the UTC time (using today as base date)
    const utcDate = new Date();
    utcDate.setUTCHours(hours, minutes, 0, 0);
    
    // Convert to local timezone
    const localTimeStr = utcDate.toLocaleString('en-US', {
      timeZone: businessTimezone,
      hour12: false,
      hour: '2-digit',
      minute: '2-digit'
    });
    
    return localTimeStr;
  } catch (error) {
    console.warn(`Failed to convert UTC time ${utcTimeStr} to timezone ${businessTimezone}:`, error);
    return utcTimeStr; // Return original on error
  }
}

/**
 * Convert display hours array from UTC to local business timezone
 */
export function convertDisplayHoursToLocal(
  utcRanges: Array<{start: string, end: string}>,
  businessTimezone: string
): Array<{start: string, end: string}> {
  return utcRanges.map(range => ({
    start: convertUtcTimeToLocal(range.start, businessTimezone),
    end: convertUtcTimeToLocal(range.end, businessTimezone)
  }));
}


/**
 * Create a timezone-aware Date object from user input time on a specific date
 * This ensures user input is interpreted in the business timezone, not browser timezone
 */
export function createBusinessTimezoneDate(
  baseDate: Date,
  hours: number,
  minutes: number,
  businessTimezone: string
): Date {
  try {
    // Create a date string in the business timezone
    const dateStr = format(baseDate, 'yyyy-MM-dd');
    const timeStr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    const dateTimeStr = `${dateStr}T${timeStr}:00`;
    
    // Create a date as if it's in the business timezone
    // This is a temporary date in the browser timezone but represents business timezone time
    const tempDate = new Date(`${dateTimeStr}`);
    
    // Get the difference between business timezone and browser timezone
    const businessOffset = getTimezoneOffsetForDate(tempDate, businessTimezone);
    const browserOffset = tempDate.getTimezoneOffset(); // Minutes behind UTC
    
    // Calculate the adjustment needed to convert from business timezone to UTC
    const adjustmentMinutes = (-browserOffset) - (businessOffset * 60);
    
    // Apply the adjustment
    const adjustedDate = new Date(tempDate.getTime() - (adjustmentMinutes * 60 * 1000));
    
    return adjustedDate;
  } catch (error) {
    console.warn(`Failed to create business timezone date:`, error);
    // Fallback to browser timezone
    const fallbackDate = new Date(baseDate);
    fallbackDate.setHours(hours, minutes, 0, 0);
    return fallbackDate;
  }
}

/**
 * Convert a Date object from UTC to business timezone
 */
export function convertUtcDateToBusinessTimezone(utcDate: Date, businessTimezone: string): Date {
  try {
    // Get the UTC time
    const utcTime = utcDate.getTime();
    
    // Get timezone offset for this specific date (handles DST)
    const businessOffset = getTimezoneOffsetForDate(utcDate, businessTimezone);
    
    // Convert to business timezone
    return new Date(utcTime + (businessOffset * 60 * 60 * 1000));
  } catch (error) {
    console.warn(`Failed to convert UTC date to business timezone:`, error);
    return utcDate; // Return original on error
  }
}


/**
 * Get timezone offset in hours for a specific date (handles DST)
 */
export function getTimezoneOffsetForDate(date: Date, timezone: string): number {
  try {
    // Use a more reliable approach with Intl.DateTimeFormat
    // Get the same moment in time represented in both UTC and the target timezone
    const utcTime = date.getTime();

    // Create formatter for the target timezone
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });

    // Format the UTC time in the target timezone
    const formattedLocal = formatter.format(date);
    const [datePart, timePart] = formattedLocal.split(', ');
    const localTimeStr = `${datePart}T${timePart}`;

    // Parse back to get what the local time would be if interpreted as UTC
    const interpretedAsUtc = new Date(localTimeStr + 'Z');

    // Calculate offset in hours
    const offsetMs = interpretedAsUtc.getTime() - utcTime;
    return Math.round(offsetMs / (1000 * 60 * 60));
  } catch (error) {
    console.warn(`Failed to get timezone offset for date:`, error);
    return 0; // Return 0 offset on error
  }
}

// ============================================================================
// Query Boundary Utilities
// ============================================================================

/**
 * Get UTC boundaries for querying shifts on a specific business day
 * This ensures we query for the correct day in the business timezone, not browser timezone
 *
 * @example
 * // Business in Europe/Bratislava (UTC+1), querying Jan 15
 * getBusinessDayQueryBounds(new Date('2024-01-15'), 'Europe/Bratislava')
 * // Returns:
 * // startUtc: 2024-01-14T23:00:00.000Z (midnight CET)
 * // endUtc: 2024-01-15T22:59:59.999Z (23:59:59 CET)
 */
export function getBusinessDayQueryBounds(
  date: Date,
  businessTimezone: string
): { startUtc: Date; endUtc: Date } {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  // Create midnight in business timezone
  const startLocal = createBusinessTimezoneDate(
    new Date(year, month, day),
    0, 0,
    businessTimezone
  );

  // Create 23:59:59.999 in business timezone
  const endLocal = createBusinessTimezoneDate(
    new Date(year, month, day),
    23, 59,
    businessTimezone
  );
  endLocal.setSeconds(59, 999);

  return {
    startUtc: startLocal,
    endUtc: endLocal
  };
}

// ============================================================================
// UI Display Utilities
// ============================================================================

/**
 * Get timezone abbreviation for display (e.g., "CET", "EST", "PST")
 */
export function getTimezoneIndicator(businessTimezone: string): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: businessTimezone,
      timeZoneName: 'short'
    });
    const parts = formatter.formatToParts(new Date());
    return parts.find(p => p.type === 'timeZoneName')?.value || businessTimezone;
  } catch {
    return businessTimezone;
  }
}

/**
 * Get full timezone name for tooltips (e.g., "Central European Time")
 */
export function getTimezoneFullName(businessTimezone: string): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: businessTimezone,
      timeZoneName: 'long'
    });
    const parts = formatter.formatToParts(new Date());
    return parts.find(p => p.type === 'timeZoneName')?.value || businessTimezone;
  } catch {
    return businessTimezone;
  }
}

/**
 * Check if business timezone differs from browser timezone
 * Used to determine if we should show a timezone indicator in the UI
 */
export function isTimezoneMismatch(businessTimezone: string): boolean {
  try {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return businessTimezone !== browserTimezone;
  } catch {
    return false;
  }
}

/**
 * Get the browser's timezone
 */
export function getBrowserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Check if a timezone string is valid
 */
export function isValidTimezone(timezone: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Format a date in business timezone for display
 */
export function formatDateInBusinessTimezone(
  date: Date,
  businessTimezone: string,
  formatOptions: Intl.DateTimeFormatOptions = {}
): string {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    timeZone: businessTimezone,
    hour12: false,
    ...formatOptions
  };

  return date.toLocaleString('en-GB', defaultOptions);
}